import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

const STORAGE_KEY = 'chat-history';
const CHAT_ID_KEY = 'chat-id';
const TENANT_ID_KEY = 'chat-tenant-id';
const TENANT_NAME_KEY = 'chat-tenant-name';
const MAX_MESSAGES = 100;

const WELCOME_MESSAGE = `Hello! 👋 Welcome to the Digital Hands CyGuard Maestro Platform.

I'm here to help you retrieve and explore your data, as well as navigate the platform.

What can I help you with today?`;

const generateChatId = (): string => {
  return crypto.randomUUID();
};

const generateMessageId = (): string => {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const createWelcomeMessage = (): ChatMessage => ({
  id: generateMessageId(),
  role: 'assistant',
  content: WELCOME_MESSAGE,
  timestamp: new Date(),
});

const getOrCreateChatId = (): string => {
  try {
    const stored = sessionStorage.getItem(CHAT_ID_KEY);
    if (stored) {
      return stored;
    }
    const newChatId = generateChatId();
    sessionStorage.setItem(CHAT_ID_KEY, newChatId);
    return newChatId;
  } catch {
    return generateChatId();
  }
};

const loadFromStorage = (): { messages: ChatMessage[] } => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const loadedMessages = parsed.messages.map((m: ChatMessage) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
      if (loadedMessages.length > 0) {
        return { messages: loadedMessages };
      }
    }
  } catch {
    // Ignore storage errors
  }
  return { messages: [createWelcomeMessage()] };
};

const saveToStorage = (messages: ChatMessage[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages }));
  } catch {
    // Ignore storage errors
  }
};

const trimMessagesToLimit = (messages: ChatMessage[]): void => {
  while (messages.length > MAX_MESSAGES) {
    messages.shift();
  }
};

export const useChatStore = defineStore('chat-store', () => {
  const stored = loadFromStorage();

  const messages = ref<ChatMessage[]>(stored.messages);
  const isOpen = ref(false);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const chatId = ref<string>(getOrCreateChatId());

  // Tenant state
  const selectedTenantId = ref<string | null>(null);
  const selectedTenantName = ref<string | null>(null);

  // Load tenant from sessionStorage on init
  try {
    const storedTenantId = sessionStorage.getItem(TENANT_ID_KEY);
    const storedTenantName = sessionStorage.getItem(TENANT_NAME_KEY);
    if (storedTenantId) selectedTenantId.value = storedTenantId;
    if (storedTenantName) selectedTenantName.value = storedTenantName;
  } catch {
    // Ignore storage errors
  }

  const getChatId = (): string => {
    return chatId.value;
  };

  const hasMessages = computed(() => messages.value.length > 0);

  const lastMessage = computed(() => (messages.value.length > 0 ? messages.value[messages.value.length - 1] : null));

  const addUserMessage = (content: string): ChatMessage => {
    const message: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    messages.value.push(message);
    trimMessagesToLimit(messages.value);
    saveToStorage(messages.value);
    return message;
  };

  const addAssistantMessage = (content: string = '', streaming: boolean = false): ChatMessage => {
    const message: ChatMessage = {
      id: generateMessageId(),
      role: 'assistant',
      content,
      timestamp: new Date(),
      isStreaming: streaming,
    };
    messages.value.push(message);
    return message;
  };

  const updateLastAssistantMessage = (content: string): void => {
    const lastMsg = messages.value[messages.value.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      lastMsg.content = content;
    }
  };

  const appendToLastAssistantMessage = (chunk: string): void => {
    const lastMsg = messages.value[messages.value.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      lastMsg.content += chunk;
    }
  };

  const finishStreaming = (): void => {
    const lastMsg = messages.value[messages.value.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      lastMsg.isStreaming = false;
    }
    isStreaming.value = false;
    trimMessagesToLimit(messages.value);
    saveToStorage(messages.value);
  };

  const setError = (errorMessage: string | null): void => {
    error.value = errorMessage;
  };

  const setIsStreaming = (streaming: boolean): void => {
    isStreaming.value = streaming;
  };

  const toggleOpen = (): void => {
    isOpen.value = !isOpen.value;
  };

  const open = (): void => {
    if (messages.value.length === 0) {
      messages.value = [createWelcomeMessage()];
    }
    isOpen.value = true;
  };

  const close = (): void => {
    isOpen.value = false;
  };

  const clearHistory = (): void => {
    messages.value = [createWelcomeMessage()];
    error.value = null;
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(CHAT_ID_KEY);
    chatId.value = getOrCreateChatId();
  };

  const setTenant = (tenantId: string, tenantName: string): void => {
    selectedTenantId.value = tenantId;
    selectedTenantName.value = tenantName;
    try {
      sessionStorage.setItem(TENANT_ID_KEY, tenantId);
      sessionStorage.setItem(TENANT_NAME_KEY, tenantName);
    } catch {
      // Ignore storage errors
    }
  };

  const switchTenant = (tenantId: string, tenantName: string): void => {
    // Atomic update: clear first, then set new tenant to avoid race conditions
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(CHAT_ID_KEY);
    messages.value = [createWelcomeMessage()];
    error.value = null;
    chatId.value = generateChatId();

    setTenant(tenantId, tenantName);

    try {
      sessionStorage.setItem(CHAT_ID_KEY, chatId.value);
    } catch {
      // Ignore storage errors
    }
  };

  return {
    messages,
    isOpen,
    isStreaming,
    error,
    chatId,
    selectedTenantId,
    selectedTenantName,
    hasMessages,
    lastMessage,
    getChatId,
    addUserMessage,
    addAssistantMessage,
    updateLastAssistantMessage,
    appendToLastAssistantMessage,
    finishStreaming,
    setError,
    setIsStreaming,
    toggleOpen,
    open,
    close,
    clearHistory,
    setTenant,
    switchTenant,
  };
});
